import { SyntheticEvent, useMemo, useState } from "react"
import { MessageCircle, Send, ThumbsUp } from "lucide-react"
import { he } from "date-fns/locale"
import { DictType } from "@pma/types"
import { useMutation } from "@tanstack/react-query"
import { toast } from "@hooks/use-toast"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { Avatar, AvatarFallback, AvatarImage } from "@components/common/avatar"
import { formatDistance } from "date-fns"
import { useDrawer } from "@stores/drawer-store"
import { formatTime, parseMessage } from "@pma/utils/helpers"
import { useAuth } from "@stores/auth-store"
import useTranslator from "@hooks/use-translator"
import { PROJECTS_PAGE_ROUTE, USERS_PAGE_ROUTE } from "@pma/utils/page-routes"
import { useNavigate } from "react-router-dom"
import { changeTypes } from "@pma/utils/data"
import {
  likeActivityApi,
  markMessageAsRead,
  readActivityApi,
  unLikeActivityApi,
  unReadActivityApi,
} from "@pma/utils/utils"
import { Popover, PopoverContent, PopoverTrigger } from "../common/popover"
import { Separator } from "@components/common/separator"
import { Badge } from "@components/common/badge"
import { Button } from "../common/button"
import { Checkbox } from "../common/checkbox"
import { Label } from "../common/label"
import axios from "axios"
import { SERVER_URL } from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"

interface Props {
  notification: DictType
  context?: "all" | "unread" | "mentioned" | "assigned" | "logs"
  onNotificationClick: () => void
}

const ActivityLogCard = ({ notification, context, onNotificationClick }: Props) => {
  const drawer = useDrawer()
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const userId = authConfig?.id
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const [isLiking, setIsLiking] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const organizationUsers = authConfig?.organizationUsers
  const [isAssignUserList, setIsAssignUserList] = useState<Boolean>(false)
  const [note, setNote] = useState<string>("")
  const [optimisticLiked, setOptimisticLiked] = useState<any>(undefined)
  const [optimisticMarkAsRead, setOptimisticMarkAsRead] = useState<any>(undefined)

  const serverLiked = notification?.liked_by_id.find((like: any) => like?.id === userId)
  const liked = optimisticLiked !== undefined ? optimisticLiked : serverLiked
  const serverMarkAsRead = notification?.readed_by_id.find((read: any) => read?.id === userId)
  const markAsRead = optimisticMarkAsRead !== undefined ? optimisticMarkAsRead : serverMarkAsRead
  const taskId = notification?.singe_action?.task_id || notification?.changes?.task_id
  const parentTaskId = notification?.singe_action?._tasks?.parent_tasks_id
  const projectId =
    notification?.singe_action?.project_id ||
    notification?.singe_action?._tasks?.project_id ||
    notification?.changes?.project_id
  const hasChanges = notification?.changes?.changes && notification?.changes?.changes?.length > 0
  const changes: DictType = hasChanges ? notification?.changes?.changes[0] : {}
  const prevValue = changes?.prev_value
  const newValue = changes?.new_value
  const changeId = notification?.changes?.change_type_id
  const changeType = changeTypes.find((o) => o.id === changeId) || {
    en: notification?.changes?._change_type?.en,
    subChange: notification?.changes?.changes
      ? notification?.changes?.changes[0]?.change_action.en
      : null,
    meta: {
      action: notification?.changes?._change_type?.en,
      entity: notification?.changes?._change_type?.en.includes("task") ? "Task" : "Project",
    },
  }

  const handleNotificationClick = () => {
    if (notification._is_read === false) {
      notificationClickMutation.mutate(notification.id)
    }

    // Open tasks sidebar for all other notification types
    if (taskId || parentTaskId) {
      context !== "logs" && drawer.onOpen()
      drawer.setContext("notes")
      drawer.setTaskId(taskId)
      navigate(PROJECTS_PAGE_ROUTE + `/${projectId}?task=${parentTaskId || taskId}`)
    } else {
      drawer.onClose()
      projectId && navigate(PROJECTS_PAGE_ROUTE + `/${projectId}`)
    }
  }

  const notificationClickMutation = useMutation({
    mutationKey: ["read-unread-notification"],
    mutationFn: (id: number) => markMessageAsRead([id]),
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: translate("Error occurred while changing message status"),
        variant: "error",
      })
    },
  })

  const parsedChanges: DictType = useMemo(() => {
    let temp = {}
    let valueKeys: any[] = []

    if (prevValue && Object.keys(prevValue).length > 0) {
      valueKeys = [...Object.keys(prevValue)]
    }
    if (newValue && Object.keys(newValue)?.length > 0) {
      valueKeys = [...Object.keys(newValue)]
    }

    if (valueKeys.includes("param_name")) {
      temp = {
        name: "status",
        from: prevValue?.name ? prevValue?.name?.replaceAll("_", " ") : prevValue?.name,
        to: newValue?.name ? newValue?.name?.replaceAll("_", " ") : newValue?.name,
        formColor: prevValue?.color,
        toColor: newValue?.color,
      }
    } else if (valueKeys.includes("title")) {
      temp = {
        name: "title",
        from: prevValue?.title,
        to: newValue?.title,
      }
    } else if (valueKeys.includes("construction_name")) {
      temp = {
        name: "construction name",
        from: prevValue?.construction_name,
        to: newValue?.construction_name,
      }
    } else if (changes?.column_name === "responsible_id") {
      temp = {
        column: changes?.column_name,
        name: "responsible user was added",
        from: prevValue.name,
        to: newValue.name,
      }
    } else if (valueKeys.includes("map_annotation")) {
      temp = {
        name: "map annotation",
        from: prevValue?.map_annotation?.annotation,
        to: newValue?.map_annotation?.annotation,
      }
    } else if (valueKeys.includes("annotation")) {
      temp = {
        name: "file annotation was added",
        from: prevValue?.annotation && "Annotation",
        to: newValue?.annotation && "Annotation",
      }
    } else if (valueKeys.includes("note") || valueKeys.includes("Note")) {
      temp = {
        name: "note",
        from: parseMessage(prevValue?.note, organizationUsers),
        to: parseMessage(newValue?.note, organizationUsers),
      }
    } else if (valueKeys.includes("address_text")) {
      temp = {
        name: "address",
        from: prevValue?.address_text,
        to: newValue?.address_text,
      }
    } else if (valueKeys.includes("file")) {
      temp = {
        name: "file was added",
        from: prevValue?.file,
        to: newValue?.file,
      }
    } else if (valueKeys.includes("finish_to") || valueKeys.includes("start_at")) {
      const changesObj = notification.changes?.changes ? notification.changes?.changes[0] : null

      if (changesObj) {
        const changeAction = changesObj.change_action

        if (changeAction[locale] === "start time was changed") {
          const oldTime = changesObj.prev_value.start_at
            ? new Date(changesObj.prev_value.start_at)
            : null
          const newTime = changesObj.new_value?.start_at
            ? new Date(changesObj.new_value?.start_at)
            : null

          if (oldTime && newTime) {
            temp = {
              name: "start date",
              from: `${oldTime.getDate()}/${oldTime.getMonth() + 1}/${oldTime.getFullYear()}`,
              to: `${newTime.getDate()}/${newTime.getMonth() + 1}/${newTime.getFullYear()}`,
            }
          }
        } else if (changeAction[locale] === "finish time was changed") {
          const oldTime = changesObj.prev_value.finish_to
            ? new Date(changesObj.prev_value.finish_to)
            : null
          const newTime = changesObj.new_value?.finish_to
            ? new Date(changesObj.new_value?.finish_to)
            : null

          if (oldTime && newTime) {
            temp = {
              name: "finish date",
              from: `${oldTime.getDate()}/${oldTime.getMonth() + 1}/${oldTime.getFullYear()}`,
              to: `${newTime.getDate()}/${newTime.getMonth() + 1}/${newTime.getFullYear()}`,
            }
          }
        }
      }
    } else if (changeType.en === "new task" && notification?._task?.title) {
      temp = {
        name: changeType.en,
        from: notification?._task?.title,
        // to: newValue && newValue[locale] ? newValue[locale] : "",
      }
    } else if (changes?.column_name === "approval") {
      temp = {
        name: "approval was changed",
        from: prevValue?.title,
        to: newValue?.title,
      }
    } else if (valueKeys.includes("description")) {
      temp = {
        name: "",
        from: prevValue?.description,
        to: newValue?.description,
      }
    } else if (changes?.column_name === "status_id") {
      temp = {
        name: "status",
        from: prevValue?.en,
        to: newValue?.en,
        formColor: prevValue?.color,
        toColor: newValue?.color,
      }
    } else {
      const changeObj = hasChanges ? notification?.changes?.changes[0] : null
      const changeAction = changeObj?.change_action ?? null

      if (changeAction && changeAction[locale]) {
        if (changeAction[locale] === "new project member") {
          temp = {
            name: changeAction[locale],
            to: newValue ? newValue.name : "",
          }
        } else {
          temp = {
            name: changeAction[locale],
            from: prevValue && prevValue[locale] ? prevValue[locale] : "",
            to: newValue && newValue[locale] ? newValue[locale] : "",
          }
        }
      }
    }

    return {
      ...temp,
      createdAt: notification?.created_at,
      updatedAt: notification?.changes_time,
    }
  }, [notification])

  if (!hasChanges) {
    return <></>
  }

  const handleAssignList = (e: SyntheticEvent) => {
    e.stopPropagation()
    setIsAssignUserList(!isAssignUserList)
  }

  const RenderAssignUser = (userArray: any) => {
    if (userArray.length >= 3) {
      return (
        <Popover>
          <div className='flex items-center'>
            {userArray?.slice(0, 2).map((user: any, index: number) => (
              <Tooltip key={index}>
                <TooltipTrigger>
                  <Avatar className='relative -ml-2 h-5 w-5'>
                    <AvatarImage
                      src={user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`}
                    />
                  </Avatar>
                </TooltipTrigger>
                <TooltipContent>
                  <h4> {user?.name}</h4>
                </TooltipContent>
              </Tooltip>
            ))}
            {userArray?.length > 2 && (
              <PopoverTrigger onClick={handleAssignList}>
                <div className='relative -ml-2 h-5 w-5 rounded-full bg-blue-100'>
                  +{userArray.length - 2}
                </div>
              </PopoverTrigger>
            )}
          </div>

          <PopoverContent className='max-h-[350px] max-w-[250px] overflow-y-auto'>
            <>
              {userArray?.length > 0 && isAssignUserList && (
                <>
                  <div className='flex w-full flex-wrap gap-2'>
                    {userArray?.map((user: any, index: number) => (
                      <Badge
                        className='relative rounded-full bg-slate-500/60 text-white'
                        variant={"outline"}
                        key={index}
                      >
                        {user.name}
                      </Badge>
                    ))}
                  </div>
                  <Separator className='my-2' />
                </>
              )}
              {userArray?.map((user: DictType, index: number) => (
                <Tooltip key={index} delayDuration={0}>
                  <TooltipTrigger asChild>
                    <div className={"flex flex-row gap-2"}>
                      <Avatar className={`ml- h-7 w-7`}>
                        <AvatarImage
                          src={
                            user.user_picture
                              ? user.user_picture.url
                              : `${process.env.PUBLIC_URL}/usr.svg`
                          }
                          alt='Avatar'
                        />
                        <AvatarFallback className='uppercase'>{user?.name}</AvatarFallback>
                      </Avatar>
                      <span className=''>{user.name}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side='bottom' className='flex items-center gap-4'>
                    {user.name}
                  </TooltipContent>
                </Tooltip>
              ))}
            </>
          </PopoverContent>
        </Popover>
      )
    } else if (userArray.length <= 2) {
      return userArray?.map((user: DictType, index: number) => (
        <Tooltip key={index} delayDuration={0}>
          <TooltipTrigger asChild>
            <div className={""}>
              <Avatar
                className={`${isHebrew && index == userArray.length - 1 ? "" : "ml-[-10px]"} h-7 w-7`}
              >
                <AvatarImage
                  src={
                    user.user_picture ? user.user_picture.url : `${process.env.PUBLIC_URL}/usr.svg`
                  }
                  alt='Avatar'
                />
                <AvatarFallback className='uppercase'>
                  {user?.first_name && user?.first_name[0]}
                  {user?.last_name && user?.last_name[0]}
                </AvatarFallback>
              </Avatar>
              <span className='sr-only'>{user.name}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side='bottom' className='flex items-center gap-4'>
            {user.name}
          </TooltipContent>
        </Tooltip>
      ))
    }
  }

  const reactToNotification = async (like: boolean) => {
    let response

    if (like) response = await unLikeActivityApi(notification.id)
    else response = await likeActivityApi(notification.id)

    return response.data
  }

  const handleReactOnActivity = async (like: boolean) => {
    // Optimistic update - immediately update UI
    if (like) {
      // Currently liked, so unlike it
      setOptimisticLiked(null)
    } else {
      // Currently not liked, so like it
      setOptimisticLiked({ id: userId })
    }

    setIsLiking(true)

    try {
      await reactToNotification(like)
      if (onNotificationClick) onNotificationClick()
      // Keep the optimistic state since API call was successful
    } catch (error: any) {
      // Revert to server state on error
      setOptimisticLiked(undefined)

      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Unable to perform this action"),
        variant: "error",
      })
    } finally {
      setIsLiking(false)
    }
  }

  const readActivity = async (read: boolean) => {
    let response

    if (read) response = await unReadActivityApi(notification.id)
    else response = await readActivityApi(notification.id)

    return response.data
  }

  const readActivityMutation = useMutation({
    mutationKey: ["read-to-notification"],
    mutationFn: (read: boolean) => readActivity(read),
    onSuccess: (data) => {
      if (onNotificationClick) onNotificationClick()
      // Keep the optimistic state since API call was successful
    },
    onError: (error: DictType) => {
      // Revert to server state on error
      setOptimisticMarkAsRead(undefined)

      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Unable to perform this action"),
        variant: "error",
      })
    },
  })

  const handleMarkAsRead = (read: boolean) => {
    // Optimistic update - immediately update UI
    if (read) {
      // Currently marked as read, so unmark it
      setOptimisticMarkAsRead(null)
    } else {
      // Currently not marked as read, so mark it
      setOptimisticMarkAsRead({ id: userId })
    }

    // Call the API
    if (!readActivityMutation.isPending) {
      readActivityMutation.mutate(read)
    }
  }

  const addNote = async (contextId: string, contextType: string, note: string) => {
    const response = await axios.post(
      SERVER_URL + "/notes/add",
      {
        note: JSON.stringify([{ insert: note }]),
        context_id: contextId,
        context_type: contextType,
      },
      {
        headers: {
          Authorization: `Bearer ${parseCookies()[ACCESS_TOKEN]}`,
        },
      },
    )

    return response.data
  }

  const handleAddNote = async () => {
    setIsSubmitting(true)
    await addNote(notification?.task_id, "task", note)
      .then((response) => {
        setNote("")
        if (onNotificationClick) onNotificationClick()
      })
      .catch((error: any) => {
        const errorMessage = error?.response?.data?.message
        toast({
          title: translate("Error"),
          description: translate(errorMessage || "Error occurred"),
          variant: "error",
        })
      })
    setIsSubmitting(false)
  }

  return (
    <div className={`mb-4 rounded-lg p-4 ${notification?._is_read ? "shadow" : "bg-primary/10"}`}>
      {/* Header */}
      <div className='mb-2 flex items-start justify-between'>
        <div className='flex items-center gap-3'>
          <div className='flex items-center justify-center text-sm font-bold uppercase'>
            <img
              src={
                notification?._user?.user_picture?.url ||
                `${process.env.PUBLIC_URL}/user-profile.svg`
              }
              alt={notification?._user?.name}
              aria-hidden='true'
              className='mr-1 h-10 w-10 cursor-pointer rounded-full'
              onClick={() => navigate(USERS_PAGE_ROUTE + `/${notification.by_user}`)}
            />
          </div>
          <div>
            <p className='font-semibold'>
              <span> {notification?._user?.name} </span>
            </p>
            <p className='text-xs text-gray-400'>
              <small>
                {notification?.created_at &&
                  formatDistance(notification?.created_at, new Date(), {
                    locale: isHebrew ? he : undefined,
                    addSuffix: true,
                  })}
              </small>{" "}
              <small> @ {formatTime(notification?.created_at, { amPm: true })} </small>
            </p>
          </div>
        </div>
        <div className='flex text-sm text-gray-400'>
          <Label className={isHebrew ? "ml-2 cursor-pointer" : "mr-2 cursor-pointer"}>
            {translate("Mark as read")}
          </Label>
          <Checkbox
            checked={markAsRead ? true : false}
            title={translate("Mark as read")}
            disabled={readActivityMutation.isPending}
            className={readActivityMutation.isPending ? "opacity-50" : ""}
            onClick={() => handleMarkAsRead(!!markAsRead?.id)}
          />
        </div>
      </div>

      {/* Project Path */}
      <div className='mb-1 text-sm'>
        {changeType.en === "new task"
          ? translate(changeType.en)
          : `${translate(changeType?.meta?.action ? changeType?.meta?.action : "Edited")} ${translate(changeType?.meta?.entity)}`}{" "}
        &gt;{" "}
        <span className='cursor-pointer text-primary' onClick={handleNotificationClick}>
          {notification?._project?.title}
        </span>
      </div>

      {/* Status Change */}
      <div className='mb-3'>
        {notification?._project?.id &&
          changeType.meta.entity === "Task" &&
          notification?._task?.title && (
            <p className='max-w-[10ch] truncate text-ellipsis text-primary md:max-w-[20ch] lg:max-w-[50ch] xl:max-w-[85ch] 2xl:max-w-[150ch]'>
              {notification?._task?.title}
            </p>
          )}

        <p className='max-w-[10ch] truncate text-ellipsis md:max-w-[20ch] lg:max-w-[50ch] xl:max-w-[85ch] 2xl:max-w-[150ch]'>
          {translate(parsedChanges?.name)}
          {parsedChanges?.from && (
            <>
              <span> {parsedChanges?.to ? translate("changed from") : translate("removed")} </span>
              <span
                className='rounded-full px-4 py-[2px] text-sm font-normal'
                style={{ background: parsedChanges?.formColor || "#transparent" }}
              >
                {parsedChanges?.from}
              </span>{" "}
              <span> {parsedChanges?.to ? <>&gt;</> : ""} </span>
            </>
          )}
          {parsedChanges?.to && (
            <>
              {parsedChanges?.name === "note" && (
                <span>
                  {" "}
                  {!parsedChanges?.from && parsedChanges?.to
                    ? translate("was added")
                    : translate("was updated")}
                  &nbsp;
                </span>
              )}

              {parsedChanges?.name !== "status" ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: parseMessage(parsedChanges?.to || "", organizationUsers),
                  }}
                  className='ml-1 max-w-[10ch] truncate text-ellipsis text-primary md:max-w-[20ch] lg:max-w-[50ch] xl:max-w-[85ch] 2xl:max-w-[150ch]'
                />
              ) : (
                <span
                  className='rounded-full px-4 py-[2px] text-sm font-normal'
                  style={{ background: parsedChanges?.toColor || "#transparent" }}
                >
                  {parsedChanges?.to}
                </span>
              )}
            </>
          )}

          {notification?._project?.id &&
            changeType.meta.entity === "Project" &&
            notification?._project?.title && (
              <span className='flex-1'>
                &nbsp;
                <Tooltip>
                  <TooltipTrigger>
                    <span className='max-w-[25ch] truncate text-ellipsis text-primary md:max-w-[20ch] lg:max-w-[150ch]'>
                      {notification?._project?.title}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className='bg-white text-black'>
                    <span className=''>{notification?._project?.title}</span>
                  </TooltipContent>
                </Tooltip>
              </span>
            )}
          {parsedChanges?.name === "note" && newValue && newValue?.file && (
            <span className='flex-1'>
              &nbsp;
              <img src={newValue?.file} height={"25px"} width={"25px"} alt='img' />
            </span>
          )}

          <>
            {notification?.singe_action?._notes[0] && (
              <Tooltip>
                <TooltipTrigger>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: parseMessage(
                        notification?.singe_action._notes[0]?.note,
                        organizationUsers,
                      ),
                    }}
                    className='xlg:max-w-[150ch] max-w-[40ch] flex-1 truncate text-ellipsis md:max-w-[100ch] lg:max-w-[120ch]'
                  />
                </TooltipTrigger>
                <TooltipContent className='bg-white text-black'>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: parseMessage(
                        notification?.singe_action._notes[0]?.note,
                        organizationUsers,
                      ),
                    }}
                    className=''
                  />
                </TooltipContent>
              </Tooltip>
            )}
            {notification?.singe_action?._tasks?.notes_id && (
              <div className='flex items-center gap-1 py-1 text-xs text-slate-400'>
                <MessageCircle size={13} /> {notification?.singe_action._tasks?.notes_id?.length}
              </div>
            )}
          </>
        </p>
      </div>

      <div className='mt-2 flex w-full cursor-pointer items-center justify-end'>
        <RenderAssignUser userArray={notification?._related_users} />
      </div>

      <div className='mt-2 flex cursor-pointer items-center justify-between border-b-2 border-t-2 border-white'>
        <div
          className='flex items-center p-2'
          title='Like Activity'
          onClick={() => {
            if (!isLiking) handleReactOnActivity(!!liked?.id)
          }}
        >
          <ThumbsUp
            className={`cursor-pointer text-primary ${isLiking ? "opacity-50" : ""}`}
            size={16}
            fill={liked ? "blue" : "none"}
          />{" "}
          <span className={isHebrew ? "pr-1" : "pl-1"}>{translate("Like")}</span>
        </div>
      </div>

      <div className='mt-2 flex align-middle'>
        <input
          placeholder={`${translate("Add new note")}...`}
          className='w-full rounded-md px-2 py-2'
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <Button
          className={isHebrew ? "mr-3" : "ml-3"}
          variant='default'
          disabled={note === "" || isSubmitting}
          loading={isSubmitting}
          onClick={handleAddNote}
        >
          <Send size={16} />{" "}
          <span className={isHebrew ? "mr-1" : "ml-1"}>{translate("Comment")}</span>
        </Button>
      </div>
    </div>
  )
}
export default ActivityLogCard
